/**
 * Linha do tempo do processo em forma gráfica:
 *  1) Andamento (fases que o processo já atingiu, pelas movimentações do tribunal)
 *  2) Atividade mês a mês (barras)
 *  3) Lista por mês com filtros claros (movimentações, intimações, atividades, documentos)
 */
import { useMemo, useRef, useState } from "react";
import { navigate } from "@/lib/router";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Card, ErrorBox, Field, FormGrid, Modal, useAct } from "./kit";
import { confirmar } from "./feedback";
import { TeorButton, type Movement } from "./movements";

export interface TlItem {
  kind: "mov" | "com" | "atv" | "doc"; id?: string; at: string; title: string; detail: string | null; source: string | null; link: string | null; historical?: boolean;
  extra?: { code?: number | null; degree?: string | null; organ?: string | null; relevant?: boolean; matchedCommunicationId?: string | null; dismissedAt?: string | null; fullText?: string; name?: string; processId?: string; teorAvailable?: boolean; hasTeor?: boolean; docIds?: string[]; teorDocumentId?: string | null; teorDocName?: string | null; hidden?: boolean; current?: boolean; manual?: boolean; originalName?: string | null };
}

const KIND: Record<TlItem["kind"], { label: string; color: string; icon: string }> = {
  mov: { label: "Movimentações", color: "#64748b", icon: "⚖" },
  com: { label: "Intimações", color: "#2563eb", icon: "✉" },
  atv: { label: "Atividades", color: "#16a34a", icon: "✓" },
  doc: { label: "Documentos", color: "#9333ea", icon: "📄" },
};

/** Fases, em ordem, reconhecidas pelo nome da movimentação (tabela do CNJ). */
export const PHASES: Array<{ key: string; label: string; re: RegExp }> = [
  { key: "distribuicao", label: "Distribuição", re: /distribu|autua[çc][ãa]o|recebid[oa] a peti[çc][ãa]o inicial/i },
  { key: "citacao", label: "Citação", re: /cita[çc][ãa]o|citad[oa]/i },
  { key: "defesa", label: "Contestação", re: /contesta[çc][ãa]o|defesa|impugna[çc][ãa]o/i },
  { key: "instrucao", label: "Instrução / audiência", re: /audi[êe]ncia|per[íi]cia|laudo|instru[çc][ãa]o/i },
  { key: "sentenca", label: "Sentença", re: /senten[çc]a|julgad[oa] (im)?procedente|procedente|improcedente|homologa/i },
  { key: "recurso", label: "Recurso", re: /recurso|apela[çc][ãa]o|agravo|embargos|ac[óo]rd[ãa]o/i },
  { key: "transito", label: "Trânsito em julgado", re: /tr[âa]nsit/i },
  { key: "cumprimento", label: "Cumprimento / pagamento", re: /cumprimento de senten|execu[çc][ãa]o|requisi[çc][ãa]o de pequeno|rpv|precat[óo]rio|alvar[áa]|pagamento/i },
  { key: "arquivado", label: "Arquivado", re: /arquiv|baixa definitiva/i },
];

export function phaseOf(title: string): string | null {
  for (const p of PHASES) if (p.re.test(title)) return p.key;
  return null;
}

const MES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const monthKey = (iso: string) => iso.slice(0, 7);
const monthLabel = (k: string) => `${MES[Number(k.slice(5)) - 1]}/${k.slice(0, 4)}`;

/** Fonte das movimentações: tribunal (MNI), DataJud ou ambas; "sem repetidas" esconde a do DataJud que já veio do tribunal. */
type SrcMode = "unica" | "tribunal" | "datajud" | "todas";
const SRC_LABEL: Record<SrcMode, string> = { unica: "Movimentações sem repetir", tribunal: "Só do tribunal (MNI e push)", datajud: "Só do DataJud", todas: "Tribunal + push + DataJud (com repetidas)" };
const dayBr = (iso: string) => new Date(new Date(iso).getTime() - 3 * 3_600_000).toISOString().slice(0, 10);
const hmBr = (iso: string) => new Date(new Date(iso).getTime() - 3 * 3_600_000).toISOString().slice(11, 16);

/** Esconde a movimentação do DataJud quando o tribunal já trouxe a mesma (mesmo dia e mesmo código ou mesmo horário). */
export function dedupMovements<T extends { kind: string; at: string; source: string | null; extra?: { code?: number | null } }>(items: T[]): T[] {
  const trib = items.filter((i) => i.kind === "mov" && (i.source === "tribunal" || i.source === "push"));
  if (!trib.length) return items;
  const keys = new Set<string>();
  for (const t of trib) { keys.add(`${dayBr(t.at)}|c${t.extra?.code ?? "?"}`); keys.add(`${dayBr(t.at)}|h${hmBr(t.at)}`); }
  return items.filter((i) => !(i.kind === "mov" && i.source === "datajud" && (keys.has(`${dayBr(i.at)}|c${i.extra?.code ?? "x"}`) || keys.has(`${dayBr(i.at)}|h${hmBr(i.at)}`))));
}

/** Edição da linha do tempo (quem pode editar o processo). */
export interface TlEdit { processId: string; showHidden: boolean; setShowHidden: (v: boolean) => void }

export function ProcessTimelineView({ items: allItems, trackFrom, edit }: { items: TlItem[]; trackFrom?: string | null; edit?: TlEdit }): JSX.Element {
  const [addMark, setAddMark] = useState(false);
  const currentItem = allItems.find((i) => i.kind === "mov" && i.extra?.current) ?? null;
  const [src, setSrc] = useState<SrcMode>(() => { try { return (localStorage.getItem("tl-src") as SrcMode) || "unica"; } catch { return "unica"; } });
  const changeSrc = (v: SrcMode) => { setSrc(v); try { localStorage.setItem("tl-src", v); } catch { /* ignore */ } };
  const items = useMemo(() => {
    if (src === "todas") return allItems;
    if (src === "tribunal") return allItems.filter((i) => i.kind !== "mov" || i.source === "tribunal" || i.source === "push");
    if (src === "datajud") return allItems.filter((i) => i.kind !== "mov" || i.source === "datajud");
    return dedupMovements(allItems);
  }, [allItems, src]);
  const [on, setOn] = useState<Record<TlItem["kind"], boolean>>({ mov: true, com: true, atv: true, doc: true });
  const [hideHist, setHideHist] = useState(false);
  const [openMonths, setOpenMonths] = useState<Set<string>>(new Set());
  const [phaseFilter, setPhaseFilter] = useState<string | null>(null);
  const [monthFilter, setMonthFilter] = useState<string | null>(null);
  const [view, setView] = useState<TlItem | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const focus = () => setTimeout(() => listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);

  // Andamento: primeira data em que cada fase apareceu.
  const phases = useMemo(() => {
    const first = new Map<string, string>();
    for (const i of [...items].filter((x) => x.kind === "mov").sort((a, b) => a.at.localeCompare(b.at))) {
      const k = phaseOf(`${i.title} ${i.detail ?? ""}`);
      if (k && !first.has(k)) first.set(k, i.at);
    }
    let last = -1;
    PHASES.forEach((p, idx) => { if (first.has(p.key)) last = idx; });
    return { first, last };
  }, [items]);

  // Barras por mês (últimos 24 meses com algo, ou todos se menos).
  const months = useMemo(() => {
    const m = new Map<string, { total: number; hist: number }>();
    for (const i of items) { const k = monthKey(i.at); const c = m.get(k) ?? { total: 0, hist: 0 }; c.total++; if (i.historical) c.hist++; m.set(k, c); }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0])).slice(-24);
  }, [items]);
  const maxM = Math.max(1, ...months.map(([, v]) => v.total));

  const rows = items.filter((i) => on[i.kind] && (!hideHist || !i.historical)
    && (!phaseFilter || (i.kind === "mov" && phaseOf(`${i.title} ${i.detail ?? ""}`) === phaseFilter))
    && (!monthFilter || monthKey(i.at) === monthFilter));
  const byMonth = new Map<string, TlItem[]>();
  for (const i of rows) { const k = monthKey(i.at); byMonth.set(k, [...(byMonth.get(k) ?? []), i]); }
  const monthKeys = [...byMonth.keys()].sort((a, b) => b.localeCompare(a));
  const histCount = items.filter((i) => i.historical).length;
  const last = items.length ? items.reduce((a, b) => (a.at > b.at ? a : b)) : null;
  const daysSince = last ? Math.floor((Date.now() - new Date(last.at).getTime()) / 86_400_000) : null;
  const allOn = Object.values(on).every(Boolean);
  // Os 3 meses mais recentes abrem; os demais ficam recolhidos.
  const isOpen = (k: string, idx: number) => (openMonths.has(k) ? true : openMonths.has(`-${k}`) ? false : idx < 3 || !!phaseFilter || !!monthFilter);
  const toggleMonth = (k: string, idx: number) => {
    const n = new Set(openMonths); n.delete(k); n.delete(`-${k}`);
    n.add(isOpen(k, idx) ? `-${k}` : k); setOpenMonths(n);
  };

  return (
    <div className="stack gap-16">
      <Card title="Andamento" actions={<span className="muted small">{last ? <>Última movimentação há <strong>{daysSince}</strong> dia(s) ({fmtDate(last.at)})</> : "Sem movimentações"}</span>}>
        <div className="phase-track">
          {PHASES.map((p, idx) => {
            const d = phases.first.get(p.key);
            const state = idx === phases.last ? "current" : d ? "done" : idx < phases.last ? "skipped" : "todo";
            return (
              <div key={p.key} className={`phase ${state} ${d ? "clickable" : ""} ${phaseFilter === p.key ? "selected" : ""}`} title={d ? `${p.label}: ${fmtDate(d)} — clique para ver as movimentações` : p.label}
                onClick={() => { if (!d) return; setPhaseFilter(phaseFilter === p.key ? null : p.key); setMonthFilter(null); setHideHist(false); setOn({ mov: true, com: true, atv: true, doc: true }); focus(); }}>
                <div className="phase-dot">{state === "done" ? "✓" : state === "current" ? "●" : ""}</div>
                <div className="phase-label">{p.label}</div>
                <div className="phase-date">{d ? fmtDate(d) : ""}</div>
              </div>
            );
          })}
        </div>
        <div className="muted small">Fases reconhecidas automaticamente pelos nomes das movimentações do tribunal. Fase sem data = não identificada (pode ter ocorrido com outro nome).</div>
        {currentItem ? (
          <div className="tl-current" onClick={() => setView(currentItem)} title="Clique para ver ou mudar">
            <span className="tl-current-tag">📍 Ponto atual</span>
            <b>{currentItem.title}</b> <span className="muted small">· {fmtDate(currentItem.at)}</span>
          </div>
        ) : edit ? <div className="muted small" style={{ marginTop: 6 }}>Dica: abra uma movimentação e use “Marcar como ponto atual” para destacar onde o processo está.</div> : null}
      </Card>

      <Card title="Linha do tempo" actions={
        <div className="chips">
          {(Object.keys(KIND) as TlItem["kind"][]).map((k) => (
            <button key={k} type="button" className={`tl-filter ${on[k] ? "on" : ""}`} style={{ ["--c" as string]: KIND[k].color }}
              onClick={() => setOn({ ...on, [k]: !on[k] })} title={on[k] ? "Clique para esconder" : "Clique para mostrar"}>
              <span className="tl-check">{on[k] ? "✓" : ""}</span>{KIND[k].label} ({items.filter((i) => i.kind === k).length})
            </button>
          ))}
          {!allOn && <button type="button" className="btn small ghost" onClick={() => setOn({ mov: true, com: true, atv: true, doc: true })}>Mostrar tudo</button>}
          <select className="input" style={{ width: "auto" }} value={src} onChange={(e) => changeSrc(e.target.value as SrcMode)} title="De onde vêm as movimentações">
            {(Object.keys(SRC_LABEL) as SrcMode[]).map((k) => <option key={k} value={k}>{SRC_LABEL[k]}</option>)}
          </select>
          {edit && <label className="check small" title="Mostra também as movimentações ocultadas"><input type="checkbox" checked={edit.showHidden} onChange={(e) => edit.setShowHidden(e.target.checked)} /> mostrar ocultas</label>}
          {edit && <button type="button" className="btn small" onClick={() => setAddMark(true)}>+ Marco manual</button>}
        </div>}>
        {months.length > 0 && (
          <div className="tl-bars" title="Quantidade de registros por mês">
            {months.map(([k, v]) => (
              <div key={k} className={`tl-bar ${monthFilter === k ? "selected" : ""}`} title={`${monthLabel(k)}: ${v.total} registro(s)${v.hist ? `, ${v.hist} antes do acompanhamento` : ""} — clique para ver`}
                onClick={() => { setMonthFilter(monthFilter === k ? null : k); setPhaseFilter(null); setHideHist(false); focus(); }}>
                <div className="tl-bar-fill" style={{ height: `${Math.max(6, (v.total / maxM) * 100)}%`, opacity: v.hist === v.total ? 0.35 : 1 }} />
                <div className="tl-bar-label">{MES[Number(k.slice(5)) - 1]}</div>
              </div>
            ))}
          </div>
        )}
        {histCount > 0 && (
          <div className="row small" style={{ margin: "8px 0" }}>
            <span className="muted">{histCount} registro(s) antes de {trackFrom ? fmtDate(trackFrom) : "o início do acompanhamento"} aparecem em cinza (histórico, sem alerta).</span>
            <button type="button" className="btn small ghost" onClick={() => setHideHist(!hideHist)}>{hideHist ? "Mostrar histórico" : "Esconder histórico"}</button>
          </div>
        )}
        {(phaseFilter || monthFilter) && (
          <div className="row small" style={{ margin: "6px 0" }}>
            <span>Mostrando: <strong>{phaseFilter ? `fase ${PHASES.find((p) => p.key === phaseFilter)?.label}` : monthLabel(monthFilter!)}</strong></span>
            <button type="button" className="btn small ghost" onClick={() => { setPhaseFilter(null); setMonthFilter(null); }}>✕ limpar</button>
          </div>
        )}
        <div ref={listRef} />
        {!rows.length ? (
          <div className="muted">{items.length ? <>Nada com os filtros atuais. <button type="button" className="btn small" onClick={() => { setOn({ mov: true, com: true, atv: true, doc: true }); setHideHist(false); }}>Mostrar tudo</button></> : "Nada registrado ainda."}</div>
        ) : (
          <div className="tl-months">
            {monthKeys.map((k, idx) => {
              const list = byMonth.get(k)!.sort((a, b) => b.at.localeCompare(a.at));
              const open = isOpen(k, idx);
              return (
                <div key={k} className="tl-month">
                  <button type="button" className="tl-month-head" onClick={() => toggleMonth(k, idx)} title="Abrir/fechar o mês">
                    <span>{open ? "▾" : "▸"} {monthLabel(k)}</span>
                    <span className="tl-month-dots">{list.slice(0, 12).map((i, n) => <span key={n} className="dot" style={{ background: KIND[i.kind].color, opacity: i.historical ? 0.4 : 1 }} />)}</span>
                    <span className="muted small">{list.length}</span>
                  </button>
                  {open && (
                    <ul className="timeline">
                      {list.map((i, n) => {
                        const ph = i.kind === "mov" ? phaseOf(`${i.title} ${i.detail ?? ""}`) : null;
                        return (
                          <li key={n} className={`t-${i.kind} ${i.historical ? "historical" : ""} ${i.extra?.hidden ? "tl-hidden" : ""} ${i.extra?.current ? "tl-is-current" : ""} tl-item`} onClick={() => setView(i)} title="Clique para ver os detalhes">
                            <div className="when">{fmtDateTime(i.at)} · <span style={{ color: KIND[i.kind].color }}>{KIND[i.kind].icon} {KIND[i.kind].label.replace(/s$/, "").replace(/õe$/, "ão")}</span>{i.source ? ` · ${i.source}` : ""}{i.historical ? " · histórico" : ""}</div>
                            <div style={{ fontWeight: ph || i.extra?.current ? 600 : 400 }}>{i.title}{ph && <span className="phase-tag">{PHASES.find((p) => p.key === ph)!.label}</span>}
                              {i.extra?.current && <span className="phase-tag tl-tag-current">📍 ponto atual</span>}
                              {i.extra?.manual && <span className="phase-tag">marco manual</span>}
                              {i.extra?.originalName && <span className="phase-tag" title={`Texto original: ${i.extra.originalName}`}>editada</span>}
                              {i.extra?.hidden && <span className="phase-tag">oculta</span>}
                            </div>
                            {i.detail && <div className="muted small tl-clamp">{i.detail}</div>}
                            {i.kind === "mov" && i.id && i.extra?.processId && (i.extra.teorAvailable || i.extra.hasTeor) && (
                              <div className="tl-doc" onClick={(e) => e.stopPropagation()}>
                                📄 {i.extra.teorDocName ? i.extra.teorDocName.replace(/\.(html?|pdf|txt|rtf)$/i, "") : `Documento ${i.extra.docIds?.[0] ?? ""}`}
                                <TeorButton m={toMovement(i)} invalidate={[["processo-linha"]]} />
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
      {view && <TimelineItemModal item={view} edit={edit} onClose={() => setView(null)} />}
      {addMark && edit && <ManualMarkModal processId={edit.processId} onClose={() => setAddMark(false)} />}
    </div>
  );
}

function toMovement(i: TlItem): Movement {
  const x = i.extra ?? {};
  return { id: i.id!, processId: x.processId!, code: x.code ?? null, name: i.title, complement: i.detail, occurredAt: i.at, degree: x.degree ?? null, organ: x.organ ?? null,
    relevant: !!x.relevant, matchedCommunicationId: x.matchedCommunicationId ?? null, dismissedAt: x.dismissedAt ?? null, teorAvailable: x.teorAvailable, hasTeor: x.hasTeor };
}

function ManualMarkModal({ processId, onClose }: { processId: string; onClose: () => void }): JSX.Element {
  const [f, setF] = useState({ name: "", complement: "", occurredAt: new Date(Date.now() - 3 * 3_600_000).toISOString().slice(0, 10), current: true });
  const m = useAct(() => api.post(`/processos/${processId}/marcos`, { name: f.name.trim(), complement: f.complement.trim() || null, occurredAt: f.occurredAt, current: f.current }), [["processo-linha"]], onClose, "Marco registrado");
  return (
    <Modal title="Novo marco manual" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={m.isPending || f.name.trim().length < 2 || !f.occurredAt} onClick={() => m.mutate()}>Salvar</button></>}>
      <FormGrid cols={1}>
        <Field label="O que aconteceu" required><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ex.: Audiência de conciliação realizada — sem acordo" autoFocus /></Field>
        <Field label="Data" required><input className="input" type="date" value={f.occurredAt} onChange={(e) => setF({ ...f, occurredAt: e.target.value })} style={{ maxWidth: 180 }} /></Field>
        <Field label="Detalhes"><textarea className="input" rows={4} value={f.complement} onChange={(e) => setF({ ...f, complement: e.target.value })} /></Field>
        <label className="check"><input type="checkbox" checked={f.current} onChange={(e) => setF({ ...f, current: e.target.checked })} /> Marcar como ponto atual do processo</label>
      </FormGrid>
      <ErrorBox error={m.error} />
    </Modal>
  );
}

function MovementEditor({ i, processId, onDone }: { i: TlItem; processId: string; onDone: () => void }): JSX.Element {
  const manual = !!i.extra?.manual;
  const [f, setF] = useState({ name: i.title, complement: i.detail ?? "", occurredAt: new Date(new Date(i.at).getTime() - 3 * 3_600_000).toISOString().slice(0, 10) });
  const m = useAct(() => api.patch(`/processos/${processId}/movimentacoes/${i.id}`, { name: f.name.trim(), complement: f.complement.trim() || null, ...(manual ? { occurredAt: f.occurredAt } : {}) }), [["processo-linha"]], onDone, "Movimentação atualizada");
  return (
    <div className="stack gap-8" style={{ marginTop: 12 }}>
      <FormGrid cols={1}>
        <Field label="Texto"><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        {manual && <Field label="Data"><input className="input" type="date" value={f.occurredAt} onChange={(e) => setF({ ...f, occurredAt: e.target.value })} style={{ maxWidth: 180 }} /></Field>}
        <Field label="Detalhes"><textarea className="input" rows={3} value={f.complement} onChange={(e) => setF({ ...f, complement: e.target.value })} /></Field>
      </FormGrid>
      {!manual && <div className="muted small">O texto original do tribunal fica guardado e continua visível como “original”.</div>}
      <ErrorBox error={m.error} />
      <div className="row gap-8" style={{ justifyContent: "flex-end" }}>
        <button className="btn" onClick={onDone}>Cancelar</button>
        <button className="btn primary" disabled={m.isPending || f.name.trim().length < 2} onClick={() => m.mutate()}>Salvar texto</button>
      </div>
    </div>
  );
}

function TimelineItemModal({ item: i, edit, onClose }: { item: TlItem; edit?: TlEdit; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const canEdit = !!edit && i.kind === "mov" && !!i.id;
  const after = () => { void qc.invalidateQueries({ queryKey: ["processo-linha"] }); onClose(); };
  const setCurrent = useAct(() => api.put(`/processos/${edit!.processId}/ponto-atual`, { movementId: i.extra?.current ? null : i.id }), [["processo-linha"]], after, i.extra?.current ? "Ponto atual removido" : "Ponto atual marcado");
  const toggleHidden = useAct(() => api.patch(`/processos/${edit!.processId}/movimentacoes/${i.id}`, { hidden: !i.extra?.hidden }), [["processo-linha"]], after, i.extra?.hidden ? "Movimentação visível de novo" : "Movimentação ocultada");
  const k = KIND[i.kind];
  const ph = i.kind === "mov" ? phaseOf(`${i.title} ${i.detail ?? ""}`) : null;
  const x = i.extra ?? {};
  const openDoc = () => { if (i.id) void api.download(`/documentos/${i.id}/download?inline=1`, x.name ?? "documento", true); };
  return (
    <Modal title={<><span style={{ color: k.color }}>{k.icon}</span> {i.title}</>} onClose={onClose} wide
      footer={<>
        <button className="btn" onClick={onClose}>Fechar</button>
        {i.kind === "doc" && i.id && <button className="btn primary" onClick={openDoc}>Abrir documento</button>}
        {i.link && <button className="btn primary" onClick={() => { onClose(); navigate(i.link!); }}>{i.kind === "com" ? "Abrir a intimação" : "Abrir a atividade"}</button>}
        {i.kind === "mov" && i.id && x.processId && <TeorButton m={toMovement(i)} invalidate={[["processo-linha"]]} />}
        {i.kind === "mov" && x.matchedCommunicationId && <button className="btn primary" onClick={() => { onClose(); navigate(`/controladoria/intimacoes/${x.matchedCommunicationId}?fila=todas`); }}>Ver a intimação correspondente</button>}
      </>}>
      {canEdit && !editing && (
        <div className="row gap-8 wrap" style={{ marginBottom: 10 }}>
          <button className="btn small" disabled={setCurrent.isPending} onClick={() => setCurrent.mutate()}>{x.current ? "Desmarcar ponto atual" : "📍 Marcar como ponto atual"}</button>
          <button className="btn small" onClick={() => setEditing(true)}>Editar texto</button>
          <button className="btn small" disabled={toggleHidden.isPending} onClick={async () => { if (x.hidden || await confirmar("Ocultar esta movimentação da linha do tempo? Ela continua guardada e pode ser mostrada de novo em “mostrar ocultas”.", { confirm: "Ocultar" })) toggleHidden.mutate(); }}>{x.hidden ? "Mostrar de novo" : "Ocultar"}</button>
        </div>
      )}
      <ErrorBox error={setCurrent.error ?? toggleHidden.error} />
      {canEdit && editing && <MovementEditor i={i} processId={edit!.processId} onDone={() => setEditing(false)} />}
      <dl className="kv">
        <dt>Quando</dt><dd>{fmtDateTime(i.at)}</dd>
        <dt>Tipo</dt><dd>{k.label.replace(/s$/, "").replace(/õe$/, "ão")}{ph && <span className="phase-tag">{PHASES.find((p) => p.key === ph)!.label}</span>}{i.historical && <> <Badge tone="muted">histórico (antes do acompanhamento)</Badge></>}</dd>
        {i.kind === "mov" && <>
          {x.organ && <><dt>Órgão</dt><dd>{x.organ}{x.degree ? ` · ${x.degree}` : ""}</dd></>}
          {x.code != null && <><dt>Código CNJ</dt><dd>{x.code}</dd></>}
          <dt>Conferência</dt><dd>{!x.relevant ? <span className="muted">não gera prazo</span> : x.matchedCommunicationId ? <Badge tone="ok">intimação capturada</Badge> : x.dismissedAt ? <Badge tone="muted">conferida</Badge> : <Badge tone="warn">sem intimação capturada</Badge>}</dd>
          <dt>Fonte</dt><dd>{i.source === "datajud" ? "DataJud (CNJ)" : i.source === "manual" ? "Marco manual (escritório)" : i.source ?? "—"}</dd>
          {x.originalName && <><dt>Texto original</dt><dd className="muted">{x.originalName}</dd></>}
          {x.hidden && <><dt>Visibilidade</dt><dd><Badge tone="muted">oculta</Badge></dd></>}
        </>}
        {i.kind !== "mov" && i.source && <><dt>Situação</dt><dd>{i.source}</dd></>}
      </dl>
      {(x.fullText || i.detail) && <div className="pre small" style={{ marginTop: 10, maxHeight: 380, overflow: "auto", background: "var(--surface)", padding: 10, borderRadius: 6 }}>{x.fullText ?? i.detail}</div>}
    </Modal>
  );
}
